import debounce from 'lodash/debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TextInput from '../../Input/TextInput';
import { AdapterProps, SearchAdapterProps } from '../types';
import InputLabel from '../../InputLabel/InputLabel';
import css from '../filters.module.scss';
import Button from '../../Button/Button';
import Icon from '../../Icon/Icon';
import clsx from 'clsx';

type Value = string;

const SearchAdapter = (
  props: AdapterProps<Value> & SearchAdapterProps<Value>,
) => {
  const { name, label, onChange, value, defaultValue = '' } = props;
  const valueFromProps = value ?? defaultValue ?? '';
  const [inputValue, setInputValue] = useState(valueFromProps);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const debouncedOnChange = useMemo(
    () =>
      debounce((nextValue: string) => {
        onChangeRef.current?.(nextValue);
      }, 250),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  // Sync from parent when it changes externally (reset/restore/clear).
  // Does not fight in-progress typing: parent value only updates after debounce.
  useEffect(() => {
    setInputValue(valueFromProps);
  }, [valueFromProps]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setInputValue(nextValue);
      debouncedOnChange(nextValue);
    },
    [debouncedOnChange],
  );

  const handleClear = useCallback(() => {
    debouncedOnChange.cancel();
    setInputValue('');
    onChangeRef.current?.('');
  }, [debouncedOnChange]);

  return (
    <div className={css.filter}>
      {label && <InputLabel className={css.label}>{label}</InputLabel>}
      <TextInput
        variant="filter"
        name={name}
        value={inputValue}
        onChange={handleChange}
      />
      <Button
        tabIndex={-1}
        onClick={handleClear}
        variant="text"
        startIcon={<Icon name="fa fa-times" />}
        className={clsx(css['remove-button'], css['clear-button'])}
      />
    </div>
  );
};

export default SearchAdapter;
